import React from 'react'
import { Card, CardHeader, CardContent, Label, Input, Textarea, Button, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getPushTemplates, setPushTemplates, PushTemplates } from '../services/pushTemplateService'

function renderPreview(tpl: string, ctx: { moeda: string; tf: string; tf_label: string; tendencia: string; qtd?: number }) {
  let out = tpl || ''
  const replacements: Record<string, string> = {
    '{moeda}': ctx.moeda,
    '{tf}': ctx.tf,
    '{tf_label}': ctx.tf_label,
    '{tendencia}': ctx.tendencia,
    '{qtd}': typeof ctx.qtd === 'number' ? String(ctx.qtd) : '',
  }
  for (const k in replacements) {
    out = out.split(k).join(replacements[k])
  }
  return out
}

export default function PushTemplateSettings() {
  const [tpl, setTpl] = React.useState<PushTemplates>({ titleIndividual: '', bodyIndividual: '', titleBatch: '', bodyBatch: '' })
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const { add: toast } = useToast()

  const sample = React.useMemo(() => ({ moeda: 'ENJUSDT', tf: '240', tf_label: '4 horas', tendencia: 'topo descendente', qtd: 3 }), [])

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try { const t = await getPushTemplates(); if (alive) setTpl(t) }
      finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false }
  }, [])

  async function onSave() {
    if (saving) return
    setSaving(true)
    try { await setPushTemplates(tpl); toast({ title: 'Templates', description: 'Salvo com sucesso.', variant: 'success', soft: true }) }
    catch (e: any) { toast({ title: 'Templates', description: e?.message || 'Falha ao salvar', variant: 'error' }) }
    finally { setSaving(false) }
  }

  async function onReload() {
    setLoading(true)
    try { const t = await getPushTemplates(); setTpl(t) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <CardHeader className="text-sm font-medium flex items-center justify-between">
        <div>Templates de Notificação (placeholders)</div>
        <div className="flex items-center gap-2 text-xs text-foreground/70">
          <Badge>{'{moeda} {tf} {tf_label} {tendencia} {qtd}'}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-foreground/70">Carregando…</div>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="block">Título (individual)</Label>
                <Input value={tpl.titleIndividual} onChange={(e)=> setTpl(p=> ({...p, titleIndividual: (e.target as HTMLInputElement).value}))} placeholder="{moeda} - {tf_label}" />
                <div className="text-xs text-foreground/70">Ex.: {'{moeda} - {tf_label}'} → {renderPreview(tpl.titleIndividual || '{moeda} - {tf_label}', sample)}</div>
              </div>
              <div className="space-y-2">
                <Label className="block">Título (agrupada)</Label>
                <Input value={tpl.titleBatch} onChange={(e)=> setTpl(p=> ({...p, titleBatch: (e.target as HTMLInputElement).value}))} placeholder="Novas oportunidades" />
                <div className="text-xs text-foreground/70">Ex.: {renderPreview(tpl.titleBatch || 'Novas oportunidades', sample)}</div>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="block">Corpo (individual)</Label>
                <Textarea rows={3} value={tpl.bodyIndividual} onChange={(e)=> setTpl(p=> ({...p, bodyIndividual: (e.target as HTMLTextAreaElement).value}))} placeholder="Buscando {tendencia}. Aproveite!" />
                <div className="text-xs text-foreground/70">Ex.: {renderPreview(tpl.bodyIndividual || 'Buscando {tendencia}. Aproveite!', sample)}</div>
              </div>
              <div className="space-y-2">
                <Label className="block">Corpo (agrupada)</Label>
                <Textarea rows={3} value={tpl.bodyBatch} onChange={(e)=> setTpl(p=> ({...p, bodyBatch: (e.target as HTMLTextAreaElement).value}))} placeholder="Tem {qtd} oportunidades novas. Confira!" />
                <div className="text-xs text-foreground/70">Ex.: {renderPreview(tpl.bodyBatch || 'Tem {qtd} oportunidades novas. Confira!', sample)}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={onSave} disabled={saving}>Salvar</Button>
              <Button variant="outline" onClick={onReload} disabled={loading || saving}>Recarregar</Button>
            </div>
            <div className="text-xs text-foreground/60">
              Dicas: use apenas os placeholders listados acima. Recomendamos títulos até ~60 caracteres e corpo até ~120–140 para evitar truncamento em iOS.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
